import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type { CollectionCard, CollectionFacets } from '../lib/types';
import { Button, EmptyState, ErrorState, LoadingGrid, Modal, Panel } from '../components/ui';
import { CollectionCardTile } from '../components/collection/CollectionCard';
import { ALL_TYPES, titleCase } from '../lib/format';

interface ReleasePreview {
  releasable: Array<{ id: string; name: string; level: number; reward: number }>;
  blocked: Array<{ id: string; name: string; reason: string }>;
  totalReward: number;
}

/**
 * A search somebody runs often enough to name.
 *
 * Stored per device rather than on the account, like the density and sort
 * preferences beside it: a saved view is a working habit on the machine you
 * breed from, and it needs no table, no migration and no endpoint. The whole
 * value is a URL query string, so restoring one is just navigating.
 */
interface SavedView {
  name: string;
  query: string;
}

const SAVED_KEY = 'collection.savedViews';

function loadSavedViews(): SavedView[] {
  try {
    const raw = JSON.parse(localStorage.getItem(SAVED_KEY) ?? '[]');
    return Array.isArray(raw) ? (raw as SavedView[]).filter((v) => v?.name && typeof v.query === 'string') : [];
  } catch {
    // A corrupt entry must not take the whole page down with it.
    return [];
  }
}

interface ListResponse {
  pokemon: CollectionCard[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Everything about *which* Pokemon are being looked at, and in what order.
 *
 * Every one of these is a server query parameter, because a breeder's
 * collection does not fit in a browser: filtering and sorting a page of 24 out
 * of 5,000 is the database's job, and anything done client-side would only be
 * filtering the page you already have.
 */
const DEFAULTS = {
  q: '', type: '', gen: '', shiny: '', sort: 'recent', page: '1',
  grove: '', gender: '', minLevel: '', maxLevel: '', nature: '', size: '',
  marking: '', team: '', favorite: '', locked: '', breeding: '', bazaar: '',
  bloodline: '', event: '', minPotential: '',
} as const;
type ViewKey = keyof typeof DEFAULTS;

/** How a live filter is written on its removable chip. */
const CHIP_LABELS: Partial<Record<ViewKey, (value: string, facets?: CollectionFacets) => string>> = {
  q: (v) => `"${v}"`,
  type: (v) => titleCase(v),
  gen: (v) => (v === '1' ? 'Kanto' : 'Johto'),
  shiny: (v) => (v === 'true' ? 'Shiny only' : 'No shinies'),
  grove: (v, f) => f?.groves.find((g) => g.id === v)?.name ?? 'Grove',
  gender: (v) => titleCase(v),
  minLevel: (v) => `Lv. ${v}+`,
  maxLevel: (v) => `Lv. ${v}-`,
  nature: (v) => v,
  size: (v) => `Size ${v}`,
  marking: (v) => (v === 'any' ? 'Any marking' : titleCase(v)),
  team: (v) => (v === 'true' ? 'My Team' : 'Not on Team'),
  favorite: (v) => (v === 'true' ? 'Favourites' : 'Not favourite'),
  locked: (v) => (v === 'true' ? 'Locked' : 'Unlocked'),
  breeding: (v) => (v === 'true' ? 'Breeding' : 'Not breeding'),
  bazaar: (v) => (v === 'true' ? 'For sale' : 'Not listed'),
  bloodline: (v, f) => f?.bloodlines.find((b) => b.id === v)?.name ?? 'Bloodline',
  event: (v) => (v === 'true' ? 'Event Pokémon' : 'No event origin'),
  minPotential: (v) => `Potential ${v}%+`,
};

/** Sort is how somebody reads their collection, not something they filtered to. */
const FILTER_KEYS = (Object.keys(DEFAULTS) as ViewKey[]).filter((k) => k !== 'sort' && k !== 'page');

export function CollectionPage() {
  /**
   * The view lives in the URL, not in component state.
   *
   * Opening a Pokemon's full page unmounts this one, so local state meant
   * coming back landed on page 1 of an unfiltered, newest-first list — the
   * collection is the one screen somebody works *through*, and losing their
   * place on every card they open is the difference between browsing and
   * starting over. `/collection?sort=iv&page=3` is what the browser's own Back
   * button restores, which is also what makes a filtered view something you
   * can bookmark or send to somebody.
   *
   * Every write is `replace`, so a session of tweaking filters leaves one
   * history entry rather than thirty — Back should leave the collection, not
   * walk backwards through the filters.
   */
  const [params, setParams] = useSearchParams();
  const read = useCallback(
    (key: ViewKey) => params.get(key) ?? DEFAULTS[key],
    [params],
  );

  const debounced = read('q');
  const page = Math.max(1, Number(read('page')) || 1);

  const update = useCallback(
    (patch: Partial<Record<ViewKey, string>>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Any change to *what* is listed starts again at page 1: page 7 of a
          // filter that now matches four Pokemon is an empty screen.
          if (!('page' in patch)) next.delete('page');
          for (const [key, value] of Object.entries(patch)) {
            // Defaults stay out of the URL, so an untouched collection is a
            // clean `/collection` rather than a row of empty parameters.
            if (!value || value === DEFAULTS[key as ViewKey]) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // The text box stays local so typing is never a round trip through the
  // router; the URL catches up once they stop.
  const [search, setSearch] = useState(debounced);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (search !== debounced) update({ q: search });
    }, 280);
    return () => window.clearTimeout(timer);
  }, [search, debounced, update]);
  // Someone clearing a chip clears the box too — otherwise the debounce puts
  // the search straight back a moment later.
  useEffect(() => setSearch(debounced), [debounced]);

  /* --- Per-device view preferences ---------------------------------------
     Density is a preference about *this screen*, not about the player, so it
     lives in localStorage beside the collapsible-panel state rather than on
     the Profile. A phone folding to compact says nothing about a desktop. */
  const [mode, setMode] = useState<'detailed' | 'compact'>(
    () => (localStorage.getItem('collection.mode') as 'detailed' | 'compact') || 'detailed',
  );
  useEffect(() => localStorage.setItem('collection.mode', mode), [mode]);

  const [advanced, setAdvanced] = useState(false);
  const [savedViews, setSavedViews] = useState<SavedView[]>(loadSavedViews);
  const { push } = useToast();

  // A native `prompt` is suppressed by some browsers and looks nothing like the
  // rest of the game, so naming a search uses the app's own dialog.
  const [namingView, setNamingView] = useState<string | null>(null);

  const saveCurrentView = () => {
    const name = (namingView ?? '').trim();
    if (!name) return;
    // Same name replaces rather than duplicating — a list with two "Shiny
    // Eevee" entries is a list you have to test to use.
    const next = [...savedViews.filter((v) => v.name !== name), { name, query: params.toString() }];
    setSavedViews(next);
    localStorage.setItem(SAVED_KEY, JSON.stringify(next));
    setNamingView(null);
    push('success', `Saved "${name}".`);
  };

  const deleteView = (name: string) => {
    const next = savedViews.filter((v) => v.name !== name);
    setSavedViews(next);
    localStorage.setItem(SAVED_KEY, JSON.stringify(next));
  };

  /* --- The two reads ------------------------------------------------------ */

  // Every filter is a query parameter, so the dependency list is the whole
  // search string: one re-read per change, and nothing filtered in the browser.
  const query = params.toString();
  const state = useAsync<ListResponse>(
    () => api.get<ListResponse>('/pokemon', Object.fromEntries(new URLSearchParams(query))),
    [query],
  );

  // The advanced panel's options — Groves and Bloodlines are the player's own,
  // and sizes/markings come from the server catalog so one added next year
  // appears without a client build.
  const facets = useAsync<CollectionFacets>(() => api.get('/pokemon/facets'), []);

  const activeFilters = useMemo(
    () => FILTER_KEYS.filter((key) => read(key) !== DEFAULTS[key]).map((key) => [key, read(key)] as const),
    [read],
  );

  const clearAll = () =>
    update(Object.fromEntries(FILTER_KEYS.map((key) => [key, ''])) as Partial<Record<ViewKey, string>>);

  /**
   * Favouriting from the grid.
   *
   * Optimistic, and safe to be: the server is still the authority (this is the
   * same `PATCH /pokemon/:id` the detail page uses, and it re-checks ownership),
   * a favourite is not destructive, and the failure path puts the star back and
   * says so. Locking is deliberately *not* optimistic anywhere — it guards
   * destructive actions, so it waits for the server to agree.
   */
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});
  const toggleFavorite = useCallback(
    async (card: CollectionCard) => {
      const next = !(optimistic[card.id] ?? card.isFavorite);
      setOptimistic((prev) => ({ ...prev, [card.id]: next }));
      try {
        await api.patch(`/pokemon/${card.id}`, { isFavorite: next });
      } catch {
        setOptimistic((prev) => ({ ...prev, [card.id]: !next }));
        push('error', 'Could not update that favourite.');
      }
    },
    [optimistic, push],
  );
  // A fresh page carries the server's answer, so the local overlay is dropped
  // rather than left to contradict it.
  useEffect(() => setOptimistic({}), [query]);

  const cards = useMemo(
    () =>
      (state.data?.pokemon ?? []).map((card) =>
        card.id in optimistic ? { ...card, isFavorite: optimistic[card.id]! } : card,
      ),
    [state.data, optimistic],
  );

  /* --- Multi-select --------------------------------------------------------
     The selection is a Set of ids kept across page turns, so a breeder can
     gather twelve Pokemon from four pages and act on them once. It is *not*
     in the URL: it is a working set, not a view, and a link that carried
     somebody else's selection into your collection would be a link that
     preselects Pokemon for a bulk action.

     There is deliberately no Select All. Clearing is one click; choosing is
     not, and the actions behind this include one that cannot be undone. */
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Map<string, CollectionCard>>(new Map());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [releasing, setReleasing] = useState<ReleasePreview | null>(null);

  const toggleSelect = useCallback((card: CollectionCard) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(card.id)) next.delete(card.id);
      else next.set(card.id, card);
      return next;
    });
  }, []);

  const leaveSelectMode = () => {
    setSelectMode(false);
    setSelected(new Map());
    setReleasing(null);
  };

  /** Every bulk action goes through here, so the reload and the toast are one. */
  const runBulk = async (action: string, groveId?: string) => {
    const ids = [...selected.keys()];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const result = await api.post<{ changed: number; message: string }>('/pokemon/bulk', {
        ids,
        action,
        ...(groveId ? { groveId } : {}),
      });
      push('success', result.message);
      setSelected(new Map());
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Releasing asks the server what it *would* do first.
   *
   * The client cannot answer it: markings, generation, bloodline founding and
   * "the only one you own" are all server-side facts. So the confirmation names
   * exactly what will go and exactly what is being refused, with the reason —
   * and the release endpoint works it out again rather than trusting this.
   */
  const previewRelease = async () => {
    setBulkBusy(true);
    try {
      setReleasing(await api.post<ReleasePreview>('/pokemon/bulk/release-preview', {
        ids: [...selected.keys()],
      }));
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not check that selection.');
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmRelease = async () => {
    setBulkBusy(true);
    try {
      const result = await api.post<{ released: number; reward: number }>('/pokemon/bulk/release', {
        ids: [...selected.keys()],
        confirm: true,
      });
      push('success', `Released ${result.released} for ${result.reward} PokéCoins.`, '◓');
      leaveSelectMode();
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not release those.');
    } finally {
      setBulkBusy(false);
    }
  };

  const field = (key: ViewKey, label: string, options: Array<[string, string]>) => (
    <div className="field" key={key}>
      <label className="field__label" htmlFor={key}>
        {label}
      </label>
      <select id={key} className="select" value={read(key)} onChange={(e) => update({ [key]: e.target.value })}>
        {options.map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">My Pokémon</h1>
          <div className="page-head__sub">
            {state.data
              ? `${state.data.total.toLocaleString()} ${activeFilters.length ? 'match your filters' : 'in your collection'}`
              : 'Loading…'}
          </div>
        </div>
        <div className="row">
          {/* "Arrange your Grove" read as though there were one. A player has
              ten, and this is where you go to organise all of them. */}
          <Link className="btn btn--info" to="/grove">
            🌱 Manage Groves
          </Link>
        </div>
      </div>

      <Panel title="Filters" raised={false}>
        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--gap-sm)' }}
        >
          <div className="field">
            <label className="field__label" htmlFor="q">
              Search
            </label>
            <input
              id="q"
              className="input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name or nickname"
            />
          </div>

          {field('type', 'Type', [['', 'All types'], ...ALL_TYPES.map((t) => [t, titleCase(t)] as [string, string])])}
          {field('gen', 'Region', [['', 'All regions'], ['1', 'Kanto'], ['2', 'Johto']])}
          {field('shiny', 'Shiny', [['', 'All'], ['true', 'Shiny only'], ['false', 'Normal only']])}
          {field('grove', 'Grove', [
            ['', 'Any Grove'],
            ...(facets.data?.groves.map((g) => [g.id, g.name] as [string, string]) ?? []),
          ])}
          {field('sort', 'Sort', [
            ['recent', 'Recently acquired'],
            ['oldest', 'Oldest first'],
            ['hatched', 'Recently hatched'],
            ['level', 'Level high → low'],
            ['levelAsc', 'Level low → high'],
            ['iv', 'Genetic potential'],
            ['size', 'Largest first'],
            ['shiny', 'Shinies first'],
            ['favorite', 'Favourites first'],
            ['dex', 'Pokédex order'],
            ['name', 'Name A–Z'],
            ['interactions', 'Most fed'],
          ])}
        </div>

        <div className="row row--wrap" style={{ marginTop: 'var(--gap-sm)' }}>
          <Button size="sm" variant="ghost" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced}>
            {advanced ? '▾ Fewer filters' : '▸ More filters'}
          </Button>
          {/* Density, remembered per device. Two options, not five. */}
          <Button
            size="sm"
            variant={mode === 'detailed' ? 'info' : 'ghost'}
            onClick={() => setMode('detailed')}
            aria-pressed={mode === 'detailed'}
          >
            Detailed
          </Button>
          <Button
            size="sm"
            variant={mode === 'compact' ? 'info' : 'ghost'}
            onClick={() => setMode('compact')}
            aria-pressed={mode === 'compact'}
          >
            Compact
          </Button>
          <Button
            size="sm"
            variant={selectMode ? 'info' : 'ghost'}
            onClick={() => (selectMode ? leaveSelectMode() : setSelectMode(true))}
            aria-pressed={selectMode}
          >
            {selectMode ? 'Done' : '☑ Select'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setNamingView('')}
            title="Save this search to reuse later"
          >
            Save search
          </Button>
        </div>

        {advanced && (
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 'var(--gap-sm)',
              marginTop: 'var(--gap-sm)',
            }}
          >
            {field('gender', 'Gender', [
              ['', 'Any'], ['male', 'Male'], ['female', 'Female'], ['genderless', 'Genderless'],
            ])}
            {field('nature', 'Nature', [
              ['', 'Any nature'],
              ...(facets.data?.natures.map((n) => [n, n] as [string, string]) ?? []),
            ])}
            {field('size', 'Size', [
              ['', 'Any size'],
              ...(facets.data?.sizes.map((s) => [s.key, s.title ?? s.label] as [string, string]) ?? []),
            ])}
            {field('marking', 'Marking', [
              ['', 'Any'],
              ['any', 'Has a marking'],
              ...(facets.data?.markings.map((m) => [m.key, m.name] as [string, string]) ?? []),
            ])}
            {field('minPotential', 'Min. potential', [
              ['', 'Any'], ['50', '50%+'], ['65', '65%+'], ['80', '80%+'], ['90', '90%+'],
            ])}
            <div className="field">
              <label className="field__label" htmlFor="minLevel">
                Level from
              </label>
              <input
                id="minLevel"
                className="input"
                type="number"
                min={1}
                max={100}
                value={read('minLevel')}
                onChange={(e) => update({ minLevel: e.target.value })}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="maxLevel">
                Level to
              </label>
              <input
                id="maxLevel"
                className="input"
                type="number"
                min={1}
                max={100}
                value={read('maxLevel')}
                onChange={(e) => update({ maxLevel: e.target.value })}
              />
            </div>
            {field('team', 'My Team', [['', 'Any'], ['true', 'On my Team'], ['false', 'Not on my Team']])}
            {field('favorite', 'Favourite', [['', 'Any'], ['true', 'Favourites'], ['false', 'Not favourites']])}
            {field('locked', 'Locked', [['', 'Any'], ['true', 'Locked'], ['false', 'Unlocked']])}
            {field('breeding', 'Breeding', [['', 'Any'], ['true', 'In the Nursery'], ['false', 'Free']])}
            {field('bazaar', 'Bazaar', [['', 'Any'], ['true', 'Listed'], ['false', 'Not listed']])}
            {field('event', 'Event origin', [['', 'Any'], ['true', 'Event Pokémon'], ['false', 'Ordinary']])}
            {facets.data && facets.data.bloodlines.length > 0 &&
              field('bloodline', 'Bloodline', [
                ['', 'Any bloodline'],
                ...facets.data.bloodlines.map((b) => [b.id, b.name] as [string, string]),
              ])}
          </div>
        )}

        {savedViews.length > 0 && (
          <div className="filter-chips" style={{ marginTop: 'var(--gap-sm)' }}>
            <span className="tiny muted mono">Saved</span>
            {savedViews.map((view) => (
              <span key={view.name} className="filter-chip filter-chip--saved">
                <button
                  type="button"
                  className="filter-chip__go"
                  onClick={() => setParams(new URLSearchParams(view.query), { replace: true })}
                >
                  {view.name}
                </button>
                <button
                  type="button"
                  className="filter-chip__x"
                  onClick={() => deleteView(view.name)}
                  aria-label={`Delete saved search ${view.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Removable chips. A breeder six filters deep needs to see what they
            asked for — a "Clear filters" button alone tells you something is
            on but never what. */}
        {activeFilters.length > 0 && (
          <div className="filter-chips" style={{ marginTop: 'var(--gap-sm)' }}>
            {activeFilters.map(([key, value]) => (
              <button
                key={key}
                type="button"
                className="filter-chip"
                onClick={() => update({ [key]: '' })}
                aria-label={`Remove filter ${key}`}
              >
                {CHIP_LABELS[key]?.(value, facets.data ?? undefined) ?? `${key}: ${value}`}
                <span className="filter-chip__x" aria-hidden>
                  ×
                </span>
              </button>
            ))}
            <Button size="sm" variant="ghost" onClick={clearAll}>
              Clear all
            </Button>
          </div>
        )}
      </Panel>

      {state.error ? (
        <ErrorState message={state.error} onRetry={state.reload} />
      ) : state.loading ? (
        <LoadingGrid count={12} height={mode === 'compact' ? 150 : 250} />
      ) : cards.length > 0 ? (
        <>
          <div className={`collection-grid ${mode === 'compact' ? 'collection-grid--compact' : ''}`}>
            {cards.map((card) => (
              <CollectionCardTile
                key={card.id}
                card={card}
                compact={mode === 'compact'}
                selectMode={selectMode}
                selected={selected.has(card.id)}
                onToggleSelect={toggleSelect}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>

          {state.data && state.data.totalPages > 1 && (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button size="sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
                ← Prev
              </Button>
              <span className="mono tiny">
                Page {state.data.page} of {state.data.totalPages}
              </span>
              <Button
                size="sm"
                disabled={page >= state.data.totalPages}
                onClick={() => update({ page: String(page + 1) })}
              >
                Next →
              </Button>
            </div>
          )}
        </>
      ) : (
        <Panel>
          <EmptyState
            art={activeFilters.length ? '◌' : '◓'}
            title={activeFilters.length ? 'No Pokémon found' : 'Your collection is empty'}
          >
            {activeFilters.length ? (
              <div className="stack-sm">
                <div>Try changing your filters.</div>
                <div>
                  <Button size="sm" onClick={clearAll}>
                    Clear filters
                  </Button>
                </div>
              </div>
            ) : (
              'Hatch your first egg and it will appear here.'
            )}
          </EmptyState>
        </Panel>
      )}

      {/* The bulk bar. Sticky at the bottom so the count and the actions stay
          reachable while scrolling a long page gathering a selection. */}
      {selectMode && (
        <div className="pxframe bulk-bar">
          <span className="bulk-bar__count">
            {selected.size} selected
          </span>
          <Button size="sm" disabled={!selected.size || bulkBusy} onClick={() => void runBulk('favorite')}>
            ★ Favourite
          </Button>
          <Button size="sm" disabled={!selected.size || bulkBusy} onClick={() => void runBulk('unfavorite')}>
            ☆ Unfavourite
          </Button>
          <Button size="sm" disabled={!selected.size || bulkBusy} onClick={() => void runBulk('lock')}>
            🔒 Lock
          </Button>
          <Button size="sm" disabled={!selected.size || bulkBusy} onClick={() => void runBulk('unlock')}>
            Unlock
          </Button>
          <select
            className="select"
            value=""
            disabled={!selected.size || bulkBusy}
            onChange={(event) => {
              if (event.target.value) void runBulk('moveToGrove', event.target.value);
              event.target.value = '';
            }}
            aria-label="Move the selection to a Grove"
          >
            <option value="">Move to Grove…</option>
            {facets.data?.groves.map((grove) => (
              <option key={grove.id} value={grove.id}>
                {grove.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={!selected.size || bulkBusy}
            onClick={() => void runBulk('removeFromGrove')}
          >
            Remove from Grove
          </Button>
          {/* Separated from the rest and never one click from done — this is
              the only action on the page the game cannot give back. */}
          <Button
            size="sm"
            variant="primary"
            disabled={!selected.size || bulkBusy}
            onClick={() => void previewRelease()}
          >
            Release…
          </Button>
          <Button size="sm" variant="ghost" disabled={!selected.size} onClick={() => setSelected(new Map())}>
            Clear
          </Button>
        </div>
      )}

      {namingView !== null && (
        <Modal onClose={() => setNamingView(null)} labelledBy="save-view-title">
          <div className="stack-sm">
            <h2 id="save-view-title">Save this search</h2>
            <p className="tiny muted">
              Every filter and the sort are stored, on this device. Restoring one is a single click.
            </p>
            <div className="field">
              <label className="field__label" htmlFor="view-name">
                Name
              </label>
              <input
                id="view-name"
                className="input"
                autoFocus
                value={namingView}
                maxLength={32}
                onChange={(event) => setNamingView(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && saveCurrentView()}
                placeholder="Shiny Eevee"
              />
            </div>
            <div className="row">
              <Button variant="ghost" onClick={() => setNamingView(null)}>
                Cancel
              </Button>
              <Button variant="go" disabled={!namingView.trim()} onClick={saveCurrentView}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* The confirmation. It names what will go and what is being refused, in
          the server's own words, because a count alone is not something anybody
          can check before agreeing to it. */}
      {releasing && (
        <Modal onClose={() => setReleasing(null)} labelledBy="bulk-release-title">
          <div className="stack-sm">
            <h2 id="bulk-release-title">Release these Pokémon?</h2>
            {releasing.releasable.length === 0 ? (
              <p>
                None of the {selected.size} you picked can be released in bulk. Everything notable is
                protected here — you can still release one from its own page.
              </p>
            ) : (
              <>
                <p>
                  <strong>{releasing.releasable.length}</strong> will be released for{' '}
                  <strong>{releasing.totalReward.toLocaleString()}</strong> PokéCoins.{' '}
                  <strong>This cannot be undone.</strong>
                </p>
                <div className="pxwell" style={{ maxHeight: 160, overflowY: 'auto', padding: 'var(--gap-xs)' }}>
                  {releasing.releasable.map((row) => (
                    <div key={row.id} className="tiny">
                      {row.name} · Lv. {row.level}
                    </div>
                  ))}
                </div>
              </>
            )}

            {releasing.blocked.length > 0 && (
              <>
                <div className="tiny muted mono">
                  {releasing.blocked.length} protected and will be kept
                </div>
                <div className="pxwell" style={{ maxHeight: 140, overflowY: 'auto', padding: 'var(--gap-xs)' }}>
                  {releasing.blocked.map((row) => (
                    <div key={row.id} className="tiny">
                      {row.name} — <span className="muted">{row.reason}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="row row--wrap">
              <Button variant="ghost" onClick={() => setReleasing(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={releasing.releasable.length === 0 || bulkBusy}
                loading={bulkBusy}
                onClick={() => void confirmRelease()}
              >
                Release {releasing.releasable.length}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
